import { ClientEvents, MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import React, { useCallback } from 'react';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { useFavoriteFoodItemMutation, useUnfavoriteFoodItemMutation } from 'src/gqlReactTypings.generated.d';
import { useCurrentUser } from 'src/shared/hooks/useCurrentUserHook';
import { useTracker } from 'src/shared/hooks/useTracker';
import Notifications from 'src/shared/Notifications';
import { useFoodItemFavoritesStoreSelectors } from 'src/ztore/food-item-favorites-store';
import { showSavedToFavoritesToast, showUnfavoritedConfirmationToast } from '../FavoriteToasts';

interface FavoriteFoodItemButtonProps {
  foodItemId: number;
  location: MealPlanAnalyticsLocation;
  dishName?: string;
  shefId?: string;
  cuisine?: string | null;
  /** Fires synchronously — caller drives the exit animation before the mutation resolves. */
  onAfterUnfavorite?: (foodItemId: number) => void;
  onAfterUndo?: (foodItemId: number) => void;
}

export const useFavoriteFoodItemButton = (props: FavoriteFoodItemButtonProps) => {
  const { foodItemId, location, dishName, shefId, cuisine, onAfterUnfavorite, onAfterUndo } = props;

  const { show: showLoginModal } = useShowLoginOrSignUpModal();
  const [currentUser] = useCurrentUser();
  const tracker = useTracker();

  const { favoriteFoodItemIndex, setFoodItemFavorited } = useFoodItemFavoritesStoreSelectors();
  const isFavorited = Boolean(favoriteFoodItemIndex[foodItemId]);

  const [favoriteFoodItem] = useFavoriteFoodItemMutation();
  const [unfavoriteFoodItem] = useUnfavoriteFoodItemMutation();

  // Always favorites (not toggle) so the Undo closure stays correct after rapid re-toggles.
  const refavorite = useCallback(async () => {
    setFoodItemFavorited(foodItemId, true);
    onAfterUndo?.(foodItemId);
    try {
      await favoriteFoodItem({ variables: { foodItemId } });
    } catch (e) {
      setFoodItemFavorited(foodItemId, false);
      Notifications.error('Error favoriting dish. Refresh and try again.');
    }
  }, [favoriteFoodItem, foodItemId, onAfterUndo, setFoodItemFavorited]);

  const onToggle = useCallback(async () => {
    const eventData = { foodItemId, location, dishName, shefId, cuisine };
    const willFavorite = !isFavorited;
    const event = willFavorite ? ClientEvents.FOOD_ITEM_FAVORITE_ADDED : ClientEvents.FOOD_ITEM_FAVORITE_REMOVED;
    const mutation = willFavorite ? favoriteFoodItem : unfavoriteFoodItem;

    setFoodItemFavorited(foodItemId, willFavorite);
    if (!willFavorite) onAfterUnfavorite?.(foodItemId);

    try {
      await mutation({ variables: { foodItemId } });
      tracker.track(event, eventData);
      if (willFavorite) {
        showSavedToFavoritesToast({ subject: 'Dish' });
      } else {
        showUnfavoritedConfirmationToast({ subject: 'Dish', onUndo: refavorite });
      }
    } catch (e) {
      setFoodItemFavorited(foodItemId, !willFavorite);
      Notifications.error(`Error ${willFavorite ? 'favoriting' : 'unfavoriting'} dish. Refresh and try again.`);
    }
  }, [
    cuisine,
    dishName,
    favoriteFoodItem,
    foodItemId,
    isFavorited,
    location,
    onAfterUnfavorite,
    refavorite,
    setFoodItemFavorited,
    shefId,
    tracker,
    unfavoriteFoodItem,
  ]);

  const onClick = async (e: React.MouseEvent<HTMLElement, MouseEvent>): Promise<boolean> => {
    // Must stopPropagation before the auth check — otherwise the dish card navigates behind the login modal.
    e.stopPropagation();
    if (!currentUser) {
      showLoginModal({
        entryHeader: 'Login or Signup to favorite dishes',
        signUpEntryPoint: location,
        preventReloadOnLogin: true,
        onSuccessfulLogin: onToggle,
        signUpCTA: 'Sign Up to favorite',
        continueCTA: 'Continue to Favorite',
        loginCTA: 'Log in to Favorite',
        hideBanner: true,
        hideGuestOption: true,
      });
      return false;
    }
    await onToggle();
    return true;
  };

  return {
    isFavorited,
    onClick,
  };
};
